/**
 * Error handling utilities for consistent error message extraction
 * across the Memara UI application.
 */

export interface ErrorResponse {
  detail?: string;
  message?: string;
  error?: {
    message?: string;
    detail?: string;
    code?: string;
  };
}

export interface AxiosErrorResponse {
  response?: {
    data?: ErrorResponse;
    status?: number;
    statusText?: string;
  };
  detail?: string;
  message?: string;
}

/**
 * Parses Memara API JSON bodies and FastAPI-style validation errors.
 * Never returns a non-string (avoids React "objects as children" crashes).
 */
export function getAxiosResponseErrorMessage(data: unknown): string | null {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const d = data as Record<string, unknown>;

  const nestedErr = d.error;
  if (typeof nestedErr === 'string') {
    return nestedErr;
  }
  if (nestedErr && typeof nestedErr === 'object') {
    const ne = nestedErr as Record<string, unknown>;
    if (typeof ne.message === 'string') {
      return ne.message;
    }
    if (typeof ne.detail === 'string') {
      return ne.detail;
    }
  }

  if (typeof d.message === 'string') {
    return d.message;
  }

  const detail = d.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg;
      if (typeof msg === 'string') {
        return msg;
      }
    }
  }

  return null;
}

/** Memara JSON error `error.code` or legacy top-level `error_code`. */
export function getMemaraApiErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const d = data as Record<string, unknown>;
  if (typeof d.error_code === 'string') {
    return d.error_code;
  }
  const nested = d.error;
  if (nested && typeof nested === 'object' && typeof (nested as { code?: string }).code === 'string') {
    return (nested as { code: string }).code;
  }
  return undefined;
}

/**
 * User-facing message from an Axios-shaped error (or similar).
 */
export function getAxiosErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  if (error == null) {
    return fallbackMessage;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error !== 'object') {
    return fallbackMessage;
  }

  const e = error as {
    response?: { data?: unknown; status?: number; statusText?: string };
    message?: string;
    code?: string;
  };

  if (e.response?.data !== undefined && e.response?.data !== null) {
    const fromBody = getAxiosResponseErrorMessage(e.response.data);
    if (fromBody) {
      return fromBody;
    }
  }

  if (typeof e.message === 'string' && e.message.length > 0) {
    return e.message;
  }

  if (e.response?.status) {
    const status = e.response.status;
    const statusText = e.response.statusText || 'Unknown error';

    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return "Access denied. You don't have permission to perform this action.";
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Validation error. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed (${status}): ${statusText}`;
    }
  }

  return fallbackMessage;
}

/**
 * Extracts a user-friendly error message from various error formats.
 * Handles backend API errors, Axios errors, and generic JavaScript errors.
 *
 * @param error - The error object (can be from API, Axios, or generic Error)
 * @param fallbackMessage - Optional fallback message if no error message is found
 * @returns A user-friendly error message string
 */
export const extractErrorMessage = (
  error: any,
  fallbackMessage: string = 'An unexpected error occurred'
): string => {
  if (!error) {
    return fallbackMessage;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error.response?.data !== undefined && error.response?.data !== null) {
    const fromBody = getAxiosResponseErrorMessage(error.response.data);
    if (fromBody) {
      return fromBody;
    }
  }

  if (typeof error.detail === 'string') {
    return error.detail;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  if (error.response?.status) {
    return getAxiosErrorMessage(error, fallbackMessage);
  }

  return fallbackMessage;
};

/**
 * Extracts error details for debugging purposes.
 * Useful for logging or development environments.
 */
export const extractErrorDetails = (error: any): {
  message: string;
  status?: number;
  code?: string;
  stack?: string;
} => {
  const message = extractErrorMessage(error);

  const data = error?.response?.data;
  let code: string | undefined;
  if (data && typeof data === 'object' && 'error' in data) {
    const err = (data as { error?: { code?: string } }).error;
    if (err && typeof err.code === 'string') {
      code = err.code;
    }
  }
  code = code || error.response?.data?.error_code || error.code;

  return {
    message,
    status: error.response?.status || error.status,
    code,
    stack: error.stack,
  };
};

/**
 * Determines if an error is a network/connectivity error
 */
export const isNetworkError = (error: any): boolean => {
  return (
    error.code === 'NETWORK_ERROR' ||
    error.code === 'ECONNREFUSED' ||
    error.message?.includes('Network Error') ||
    error.message?.includes('fetch') ||
    !error.response
  );
};

/**
 * Determines if an error is an authentication error
 */
export const isAuthError = (error: any): boolean => {
  return (
    error.response?.status === 401 ||
    error.status === 401 ||
    error.message?.includes('Unauthorized') ||
    error.message?.includes('Authentication')
  );
};

/**
 * Determines if an error is a validation error
 */
export const isValidationError = (error: any): boolean => {
  return (
    error.response?.status === 422 ||
    error.status === 422 ||
    error.response?.data?.error?.code === 'VALIDATION_ERROR'
  );
};
